export type CtraderCsvFormatKind = "statement_deals" | "missing_deals" | "unknown";

function normalizeHeaderToken(raw: string): string {
  return raw
    .replace(/^\uFEFF/, "")
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function parseCsvLineCells(line: string): string[] {
  return line.split(",").map((cell) => cell.trim().replace(/^["']|["']$/g, ""));
}

export function detectCtraderCsvFormat(fileText: string): CtraderCsvFormatKind {
  const lines = fileText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.some((line) => normalizeHeaderToken(line) === "deals")) {
    return "missing_deals";
  }
  return "statement_deals";
}

function findDealsHeaderLine(fileText: string): string | null {
  const lines = fileText.split(/\r?\n/).map((line) => line.trim());
  for (let i = 0; i < lines.length; i += 1) {
    if (normalizeHeaderToken(lines[i]) !== "deals") continue;
    for (let j = i + 1; j < lines.length; j += 1) {
      const candidate = lines[j];
      if (!candidate) continue;
      if (
        normalizeHeaderToken(candidate) === "positions" ||
        normalizeHeaderToken(candidate) === "transactions" ||
        normalizeHeaderToken(candidate) === "summary" ||
        normalizeHeaderToken(candidate) === "balance"
      ) {
        break;
      }
      if (candidate.includes(",")) return candidate;
    }
  }
  return null;
}

export function ctraderDealsHeaderTokens(fileText: string): string[] {
  const header = findDealsHeaderLine(fileText);
  if (!header) return [];
  return parseCsvLineCells(header).map(normalizeHeaderToken);
}

export function ctraderCsvHasOpeningTimeColumn(fileText: string): boolean {
  return ctraderDealsHeaderTokens(fileText).some((token) => token.includes("openingtime"));
}

export function ctraderCsvHasNonBreakingSpaces(fileText: string): boolean {
  return fileText.includes("\u00a0");
}

/** IC Markets / cTrader statements often use NBSP in "1 Lots" and "9 979.96". */
export function sanitizeCtraderCsvText(fileText: string): string {
  return fileText.replace(/\u00a0/g, " ");
}

export function validateCtraderCsvForImport(fileText: string): {
  ok: boolean;
  kind: CtraderCsvFormatKind;
  title?: string;
  message?: string;
} {
  const kind = detectCtraderCsvFormat(fileText);

  if (kind === "missing_deals") {
    return {
      ok: false,
      kind,
      title: "Not a cTrader statement CSV",
      message:
        "This file does not contain a Deals section. In cTrader, open History → Statement, " +
        "choose your date range, then Save as CSV. The file should start with a Deals table.",
    };
  }

  if (!ctraderCsvHasOpeningTimeColumn(fileText)) {
    const header = findDealsHeaderLine(fileText) ?? "";
    const hasClosingTime = /closing\s*time/i.test(header);
    return {
      ok: false,
      kind,
      title: "Missing Opening Time column",
      message: hasClosingTime
        ? "Your export has Closing Time but not Opening Time. In cTrader History, right-click any " +
            "column header, tick Opening time (and Order ID, Closing Time, Net P&L), " +
          "then click Statement → Save as CSV again. Tessera needs both opening and closing times " +
          "to import closed trades."
        : "The Deals table is missing an Opening Time column. In cTrader statement column settings " +
          "(right-click History column headers), enable Order ID, Opening Time, Closing Time, and " +
          "Net P&L, then export the statement CSV again.",
    };
  }

  return { ok: true, kind };
}

export function ctraderCsvImportStatsAreEmpty(payload: unknown): boolean {
  const stats =
    payload && typeof payload === "object"
      ? ((payload as Record<string, unknown>).sync_stats ??
        (payload as Record<string, unknown>).syncStats)
      : null;
  if (!stats || typeof stats !== "object") return true;
  const row = stats as Record<string, unknown>;
  const meta =
    payload && typeof payload === "object"
      ? ((payload as Record<string, unknown>).report_meta ??
        (payload as Record<string, unknown>).reportMeta)
      : null;
  const dealsCount =
    meta && typeof meta === "object"
      ? Number((meta as Record<string, unknown>).deals_count ?? (meta as Record<string, unknown>).dealsCount)
      : NaN;
  if (Number.isFinite(dealsCount) && dealsCount > 0) return false;

  const nums = ["created", "Created", "updated", "Updated", "total", "Total"].map((key) =>
    Number(row[key])
  );
  return !nums.some((n) => Number.isFinite(n) && n > 0);
}

export function ctraderCsvImportZeroTradesHint(fileText: string): string {
  if (!ctraderCsvHasOpeningTimeColumn(fileText)) {
    return (
      "No trades were imported. Re-export from cTrader with Order ID, Opening Time, Closing Time, " +
      "and Net P&L enabled in statement column settings, then upload the new CSV."
    );
  }
  if (ctraderCsvHasNonBreakingSpaces(fileText)) {
    return (
      "No trades were imported. If the file looks correct, try re-exporting from cTrader or " +
      "confirm the account number, executing broker (IC Markets), and asset classes match this statement."
    );
  }
  return (
    "No trades were imported. Check the account number and executing broker on the import form, " +
    "and confirm the statement date range includes closed deals."
  );
}
